import { type DocumentType, DocumentTypeLabels } from '@nestjs-boilerplate/types';
import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatPence, SERVICES, useCheckBalance } from '@/features/payments';
import {
  BalanceDisplay,
  InsufficientBalance,
} from '@/features/payments/components/insufficient-balance';

import { PRICE_LOADING_PLACEHOLDER } from '../lib/tier-pricing';

interface RequestDocsModalProps {
  open: boolean;
  clientEmail?: string;
  onPurchase: (email: string, documentTypes: string[], otherDescription?: string) => void;
  onSkip: () => void;
  onAddFunds: () => void;
  isPurchasing: boolean;
}

const documentTypes: DocumentType[] = [
  'IDENTITY_DOCUMENT',
  'PROOF_OF_ADDRESS',
  'PROOF_OF_INCOME',
  'BANK_STATEMENT',
  'TAX_RETURN',
  'OTHER',
];

const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Request Documentation Modal - shown after skipping report purchase.
 * Generates a secure upload link and sends to client email.
 * Charges £0.50 per request.
 */
export function RequestDocsModal({
  open,
  clientEmail: initialEmail = '',
  onPurchase,
  onSkip,
  onAddFunds,
  isPurchasing,
}: RequestDocsModalProps) {
  const [email, setEmail] = useState(initialEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<DocumentType[]>([]);
  const [otherDescription, setOtherDescription] = useState('');
  const emailId = useId();
  const otherDescriptionId = useId();

  const { data: balanceCheck, isLoading: isCheckingBalance } = useCheckBalance(
    SERVICES.DOCUMENTATION_REQUEST,
    open,
  );

  const hasSufficientBalance = balanceCheck?.sufficient ?? false;
  const currentBalance = balanceCheck?.currentBalancePence ?? 0;
  const priceLabel = balanceCheck
    ? formatPence(balanceCheck.requiredAmountPence)
    : PRICE_LOADING_PLACEHOLDER;

  const emailError = emailTouched && email && !isValidEmail(email);

  // Reset form state when modal opens
  useEffect(() => {
    if (open) {
      setEmail(initialEmail);
      setEmailTouched(false);
      setSelectedTypes([]);
      setOtherDescription('');
    }
  }, [open, initialEmail]);

  const handleToggleType = (type: DocumentType) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
      if (type === 'OTHER') {
        setOtherDescription('');
      }
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const isOtherSelected = selectedTypes.includes('OTHER');
  const otherDescriptionRequired = isOtherSelected && !otherDescription.trim();

  const canPurchase =
    !!balanceCheck &&
    hasSufficientBalance &&
    email &&
    isValidEmail(email) &&
    selectedTypes.length > 0 &&
    !otherDescriptionRequired &&
    !isPurchasing;

  const handlePurchase = () => {
    if (canPurchase) {
      const otherDesc = selectedTypes.includes('OTHER') ? otherDescription.trim() : undefined;
      onPurchase(email, selectedTypes, otherDesc);
    }
  };

  const getSelectedLabel = () => {
    if (selectedTypes.length === 0) return 'Select documents...';
    if (selectedTypes.length === 1) return DocumentTypeLabels[selectedTypes[0]];
    return `${selectedTypes
      .map((t) => DocumentTypeLabels[t])
      .slice(0, 2)
      .join(', ')}...`;
  };

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-[500px]" hideCloseButton>
        <DialogHeader className="pt-4 text-center sm:text-center">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Request Documentation
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2">
          <div className="flex items-center justify-between">
            <span className="text-[16px] font-medium text-[#051923]">
              All Documentation Requests
            </span>
            <span className="text-[16px] font-medium text-[#051923]">{priceLabel}</span>
          </div>
          <p className="mt-2 text-[14px] text-muted-foreground">
            Generate a secure link to your client via email for them to provide documentation. All
            documents uploaded by your client will be flagged for review in the dashboard.
          </p>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor={emailId} className="text-[14px] font-medium text-[#051923]">
              Client Email
            </Label>
            <Input
              id={emailId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="client@example.com"
              className={`mt-1 ${emailError ? 'border-red-500' : ''}`}
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-500">Please enter a valid email address</p>
            )}
          </div>

          <div>
            <Label className="text-[14px] font-medium text-[#051923]">
              Select Document Type(s)
            </Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-1 w-full justify-between text-left font-normal"
                >
                  <span className="truncate">{getSelectedLabel()}</span>
                  <div className="flex items-center gap-2">
                    {selectedTypes.length > 0 && (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#131313] text-[12px] text-white">
                        {selectedTypes.length}
                      </span>
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[--radix-dropdown-menu-trigger-width]"
              >
                {documentTypes.map((type) => (
                  <button
                    type="button"
                    key={type}
                    className="flex w-full cursor-pointer items-center gap-2 px-2 py-2 hover:bg-muted"
                    onClick={() => handleToggleType(type)}
                  >
                    <Checkbox checked={selectedTypes.includes(type)} />
                    <span>{DocumentTypeLabels[type]}</span>
                  </button>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isOtherSelected && (
            <div>
              <Label
                htmlFor={otherDescriptionId}
                className="text-[14px] font-medium text-[#051923]"
              >
                Please specify document type
              </Label>
              <Input
                id={otherDescriptionId}
                type="text"
                value={otherDescription}
                onChange={(e) => setOtherDescription(e.target.value)}
                placeholder="e.g., Business license, Certificate of incorporation"
                className="mt-1"
              />
            </div>
          )}

          {/* Balance check */}
          {balanceCheck &&
            !isCheckingBalance &&
            (hasSufficientBalance ? (
              <BalanceDisplay currentBalancePence={currentBalance} />
            ) : (
              <InsufficientBalance
                currentBalancePence={currentBalance}
                requiredAmountPence={balanceCheck.requiredAmountPence}
                shortfall={balanceCheck.shortfall}
                onAddFunds={onAddFunds}
              />
            ))}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={handlePurchase}
            disabled={!canPurchase || isCheckingBalance}
            className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[16px] font-medium text-white hover:bg-[#131313]/90 disabled:opacity-50"
          >
            {isPurchasing ? 'Processing...' : 'Generate Secure Link'}
          </Button>
          <button
            type="button"
            onClick={onSkip}
            disabled={isPurchasing}
            className="text-[14px] font-medium text-gray-500 underline hover:text-gray-700"
          >
            Skip — finish screening
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
